import React, {useEffect, useRef, useState} from 'react';
import {useAuth} from "../../services/AuthProvider";
import styles from "../StartGame/StartGame.module.css";
import InfoIcon from "../../resources/common/exclamation_circle.png";
import IOSDownloadImage from "../../resources/Footer/Download_on_the_App_Store_Badge_.png";
import AndroidDownloadImage from "../../resources/Footer/google-play-badge_.png";
import MobileScreenshotImage from "../../resources/StartGame/mobile_screenshot.png";
import LoginRequiredPage from "../ErrorPages/LoginRequiredPage";
import {useError} from "../../services/ErrorContext";

const gameAPI = process.env.REACT_APP_API_END_POINT!;
const gameAPIKEY = process.env.REACT_APP_API_KEY!;

const UnityGameReloaded: React.FC<{ SPARXVersion: string; gameConfigUrl: string; }> = ({ SPARXVersion, gameConfigUrl }) => {
  const gameInstanceRef = useRef<any>(null); // Ref to store the game instance
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const loadingBarRef = useRef<HTMLDivElement>(null);
  const fullscreenButtonRef = useRef<HTMLDivElement>(null);
  const game1URL = window.location.origin + '/sparx_webgl';
  const game2URL = window.location.origin + '/SoW2_WebGL';

  const unityUrl = SPARXVersion.indexOf('2') !== -1 ? game2URL : game1URL;

  useEffect(() => {
    // Load Unity style from the provided URL
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `${unityUrl}/TemplateData/style.css`;
    document.head.appendChild(link);

    if (SPARXVersion.indexOf('2') !== -1) {
      // For SPARX Version 2
      // Unity build paths and config
      const buildUrl = `${unityUrl}/Build`;
      const loaderUrl = buildUrl + "/SoW2_WebGL.loader.js";
      const config: any = {
        dataUrl: buildUrl + "/SoW2_WebGL.data",
        frameworkUrl: buildUrl + "/SoW2_WebGL.framework.js",
        codeUrl: buildUrl + "/SoW2_WebGL.wasm",
        streamingAssetsUrl: `${unityUrl}/StreamingAssets`,
        companyName: "National Institute for Health Innovation",
        productName: "SPARX 2",
        productVersion: "1.0.23",
      };
      const container = document.querySelector("#unity-container");
      const canvas = canvasRef.current;
      const progressBarFull = progressBarRef.current;
      const loadingBar = loadingBarRef.current;
      const fullscreenButton = fullscreenButtonRef.current;

      // Detect mobile devices and adjust configuration
      if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        if (container) {
          container.className = "unity-mobile";
        }
        config.devicePixelRatio = 1;
      } else if (canvas) {
        canvas.style.width = "960px";
        canvas.style.height = "600px";
      }

      if (loadingBar) {
        loadingBar.style.display = "block";
      }

      // Load the Unity WebGL script
      const script = document.createElement("script");
      script.src = loaderUrl;
      script.onload = async () => {
        try {
          // Create Unity instance
          const gameInstance = await (window as any).createUnityInstance(canvas, config, (progress: number) => {
            if (progressBarFull) {
              progressBarFull.style.width = 100 * progress + "%";
            }
          });

          (window as any).unityGameLoaded = async function(objectName: any) {
            // Send the game config to Unity
            console.log("send message with gameConfig:", gameConfigUrl);
            gameInstance.SendMessage('WebGL', "setConfig", gameConfigUrl);
            gameInstanceRef.current = gameInstance; // Store game instance in ref

            if (loadingBar) {
              loadingBar.style.display = "none";
            }

            // Set up fullscreen button functionality
            if (fullscreenButton) {
              fullscreenButton.onclick = () => {
                gameInstance.SetFullscreen(1);
              };
            }
          };

          (window as any).gameLevelEnd = function () {
            window.location.reload();
          };

          (window as any).gamelog = function (msg: any) {
            console.log(msg);
          };
        } catch (errormessage) {
          alert(errormessage);
        }
      };
      document.body.appendChild(script);

      // Cleanup function: unload the game when the component unmounts
      return () => {
        if (gameInstanceRef.current) {
          gameInstanceRef.current.Quit().then(() => {
            console.log("Unity instance closed.");
            window.location.reload();
          });
        }
        document.head.removeChild(link);
        document.body.removeChild(script);
      };
    } else {
      // For SPARX Version 1
      const script = document.createElement("script");
      script.src = `${unityUrl}/Build/UnityLoader.js`;
      script.onload = () => {
        const unityInstance = (window as any).UnityLoader.instantiate(
          "gameContainer",
          `${unityUrl}/Build/SoW2_WebGL.json`
        );

        (window as any).unityGameLoaded = async function(objectName: any) {
          console.log("Unity Game loaded:", objectName);
          // Send the game config to Unity
          console.log("send message with gameConfig:", gameConfigUrl);
          unityInstance.SendMessage(objectName, "setConfig", gameConfigUrl);
          gameInstanceRef.current = unityInstance; // Store game instance in ref
        };
      };
      document.body.appendChild(script);

      // Cleanup: remove script if the component unmounts
      return () => {
        if (gameInstanceRef.current) {
          window.location.reload();
        }

        document.head.removeChild(link);
        document.body.removeChild(script);
      };
    }
  }, [SPARXVersion, unityUrl, gameConfigUrl]);

  return (
    SPARXVersion.indexOf('2') !== -1 ? (
      <div id="unity-container" className="unity-desktop" style={{ position: "static", transform: 'none' }}>
        <canvas id="unity-canvas" ref={canvasRef}></canvas>
        {/*<div id="unity-loading-bar" ref={loadingBarRef}>*/}
        {/*  <div id="unity-logo"></div>*/}
        {/*  <div id="unity-progress-bar-empty">*/}
        {/*    <div id="unity-progress-bar-full" ref={progressBarRef}></div>*/}
        {/*  </div>*/}
        {/*</div>*/}
        <div id="unity-footer">
          <div id="unity-webgl-logo"></div>
          <div id="unity-fullscreen-button" ref={fullscreenButtonRef}></div>
          <div id="unity-build-title">Sparx</div>
        </div>
      </div>
    ) : (
      <div className="webgl-content" style={{ position: "static", transform: 'none' }}>
        <div id="gameContainer" style={{ width: "960px", height: "600px" }}></div>
        <div className="footer">
          <div className="webgl-logo"></div>
          <div className="fullscreen" onClick={() => gameInstanceRef.current?.SetFullscreen(1)}></div>
          <div className="title">SPARX</div>
        </div>
      </div>
    )
  );
};

const StartSPARX: React.FC = () => {
  const { isLoggedIn, userProfile, isIPBlocked, logout } = useAuth();
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const { setError } = useError();
  const [gameConfigUrl, setGameConfigUrl] = useState('');
  const [SPARXVersion, setSPARXVersion] = useState('');
  const initGameConfigCalled = useRef(false);

  useEffect(() => {
    if (!userProfile) {
      return;
    }

    if (!initGameConfigCalled.current) {
      initGameConfigCalled.current = true; // Prevent future calls

      // build the config
      const accessToken = localStorage.getItem('access_token');
      const expiresIn = localStorage.getItem('expires_in');
      const refreshToken = localStorage.getItem('refresh_token');
      let gameConfig = `${gameAPI}/config?apiKey=${gameAPIKEY}&accessToken=${accessToken}&expiresIn=${expiresIn}&refreshToken=${refreshToken}`;

      if (userProfile.habitsId) {
        const habits= localStorage.getItem('habits');
        const habitsInfo = JSON.parse(habits!);
        if (habitsInfo && habitsInfo.uuid === userProfile.habitsId) {
          gameConfig += `&hid=${habitsInfo.uuid}&habitsAccessToken=${habitsInfo.idToken}&habitsRefreshToken=${habitsInfo.refreshToken}`;
        } else {
          // habits user didn't sign in properly, log the user out.
          setError("Sorry, there's something wrong with your credential, please re-login and try again.");
          logout();
        }
      }

      // set the variables
      setGameConfigUrl(gameConfig);
      // TODO 0: TEST SPARX1?
      // setSPARXVersion('SPARX1');
      setSPARXVersion((!!isIPBlocked || !!userProfile.habitsId) ? 'SPARX1' : 'SPARX2');
    }
  }, [gameConfigUrl, userProfile, setError, logout, isIPBlocked, SPARXVersion]);

  return (
    !isLoggedIn ? <LoginRequiredPage /> :
      <div data-testid="StartSparx" className={styles.StartGame}>
        {
          isMobile ? <div className={styles.StartGameMobile}>
              <img src={InfoIcon} alt="info"/>
              <br/>
              <div>
                <p>Web version of SPARX 2 only supported on PC and Mac.</p>
                <p>If you are using a mobile device, Please download SPARX 2 from App store or Play Store to play SPARX.</p>
              </div>
              <br/><br/>
              <div className={styles.StartGameMobileDownloadAppContainer}>
                <a target="_blank" rel="noopener noreferrer" href={SPARXVersion === 'SPARX1' ? process.env.REACT_APP_SPARX_GAME_APP_STORE : process.env.REACT_APP_SPARX_GAME_APP_STORE2}>
                  <img src={IOSDownloadImage} alt="Apple store" className={styles.StartGameAppStoreButtons} />
                </a>
                <a target="_blank" rel="noopener noreferrer" href={SPARXVersion === 'SPARX1' ? process.env.REACT_APP_SPARX_GAME_PLAY_STORE : process.env.REACT_APP_SPARX_GAME_PLAY_STORE2}>
                  <img src={AndroidDownloadImage} alt="Andriod play store" className={styles.StartGameAppStoreButtons} />
                </a>
                <img src={MobileScreenshotImage} alt="screenshot" style={{width: '90%', margin: '30px auto'}}/>
              </div>
            </div> :
            <section id="StartGameSparx" className={styles.StartGameWelcome}>
              <div style={{width: 'fit-content', marginLeft: 'auto', marginRight: 'auto'}}>
                {
                  gameConfigUrl && SPARXVersion &&
                  <UnityGameReloaded gameConfigUrl={gameConfigUrl} SPARXVersion={SPARXVersion}/>
                }
              </div>
            </section>
        }
      </div>
  );
};

export default StartSPARX;
